/**
 * Типи й сталі накладного шару (components.md, розд. 8a): спільні для статичної частини (`Popover.tsx`,
 * `Menu.tsx`, `OverflowRow.tsx`) і лінивого модуля шарів (`popover-layer.ts`). Без імпортів
 * компонентів: так лінивий модуль не імпортує статичну частину, і циклів залежностей немає.
 */
import type { HTMLAttributes, ReactNode, RefObject } from 'react';
import type { LayerAlign, LayerSide } from './anchored-layer.ts';

export type LayerCloseReason = 'escape' | 'outside';

export interface AnchoredLayerOptions {
  open: boolean;
  /** Елемент, від якого шар відкрито: позиція, «клік поза» і повернення фокуса. */
  anchor: RefObject<HTMLElement | null>;
  /** Шар просить закритися; стан відкриття тримає власник. */
  onClose: (reason: LayerCloseReason) => void;
  /** Вирівнювання за краєм якоря: `start` — лівим, `end` — правим. */
  align?: LayerAlign | undefined;
  /** Бажаний бік; інший — лише коли тут тісно, а там просторіше. */
  side?: LayerSide | undefined;
  /** `anchor` — ширина шару дорівнює ширині якоря (список під полем). */
  width?: 'auto' | 'anchor' | undefined;
  /**
   * `self` — місце дає клас шару (бічна панель під верхньою панеллю, як центр сповіщень): примітив
   * не ставить шар біля якоря й не ховає його до завантаження поведінки, а дає портал, `Esc`,
   * «клік поза» і фокус. Типово `anchor`.
   */
  place?: 'anchor' | 'self' | undefined;
}

/**
 * Зони тостів (тости дій `ui/notify` і зони з ознакою `data-toast-zone`) — над шарами
 * (`--cl-z-toast`): клік у тості («Скасувати» після дії в шарі) — не «клік поза» шаром.
 */
export const TOAST_ZONES = '.toast-region, [data-toast-zone]';

/** Клік у зоні тостів (`TOAST_ZONES`): шар від нього не закривається. */
export const inToastZone = (target: EventTarget | null): boolean =>
  target instanceof Element && target.closest(TOAST_ZONES) !== null;

export type PopoverTag = 'div' | 'ul' | 'fieldset';

export interface PopoverProps
  extends AnchoredLayerOptions,
    Omit<HTMLAttributes<HTMLElement>, 'children'> {
  children: ReactNode;
  /** Елемент шару: `ul` — перелік посилань, `fieldset` — група полів. */
  as?: PopoverTag | undefined;
  /** `ref` шару — для фокуса й пошуку пунктів власником. */
  layerRef?: RefObject<HTMLElement | null> | undefined;
  'data-testid'?: string | undefined;
}

/** Що в комірці рядка бере фокус (кнопка «Ще», елемент, вибраний з «Ще»). */
export const FOCUSABLE = 'a[href], button:not([disabled]), input, select, [tabindex]';

/** Пункт меню `MenuButton` (`Menu.tsx`). */
export type MenuItem =
  | {
      kind: 'item';
      id: string;
      label: string;
      hint?: string;
      /** `danger` — незворотна чи небезпечна дія («Видалити…»): червоний текст. */
      tone?: 'danger';
      onSelect: () => void;
    }
  | {
      kind: 'radio' | 'checkbox';
      id: string;
      label: string;
      checked: boolean;
      onSelect: () => void;
    }
  | { kind: 'group'; id: string; label: string }
  | { kind: 'separator'; id: string };

/** Елемент рядка `<OverflowRow>` (`OverflowRow.tsx`). */
export interface OverflowEntry {
  key: string;
  node: ReactNode;
  /** Пункти «Ще», коли елемент поза рядком (дія з підменю — групою пунктів). */
  menu: readonly MenuItem[];
  /** Назва для підпису «Ще» програмі читання. */
  name: string;
  /**
   * Вибір з «Ще» робить елемент вибраним у рядку (чип-перемикач): фокус переходить на нього.
   * Без цього фокус лишається на «Ще» (дія, перехід).
   */
  refocus?: boolean;
}
